// frontend/components/notifications/NotificationInbox.tsx
//
// 사용자 알림함(Inbox).
//
// 배경: 알림이 SSE 토스트로 한 번 스쳐 지나가고 끝이라, 그 순간 화면을 보고 있지 않으면
//       무슨 알림이 왔는지 다시 볼 방법이 없었다. 관리자 전용 알림 종(AdminNotificationBell)은
//       있었지만 일반 사용자에게는 아무것도 없었다.
//
// 폴링하지 않는다 — 서버가 알림을 적재하면 SSE 로 `notification-inbox` 신호를 보내고,
// 그때만 다시 조회한다. (관리자 종은 60초 폴링이지만 이쪽은 이미 SSE 연결이 있으므로 불필요)

"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useRouter } from "next/navigation";
import { Bell, Check, CheckCheck, Trash2, X, AtSign, MessageSquare, CircleCheck, Info } from "lucide-react";
import { API_URL, authOnlyHeaders } from "../../utils/api";
import { formatRelativeTime } from "../../utils/dateUtils";

type Notification = {
  id: string;
  type: "REPLY" | "MENTION" | "DEPT_MENTION" | "RESOLVE" | "GENERAL";
  message: string;
  url: string | null;
  docId: string | null;
  isRead: boolean;
  createdAt: string;
};

const TYPE_META: Record<Notification["type"], { icon: typeof Bell; color: string; label: string }> = {
  REPLY:        { icon: MessageSquare, color: "text-blue-500",   label: "답글" },
  MENTION:      { icon: AtSign,        color: "text-violet-500", label: "멘션" },
  DEPT_MENTION: { icon: AtSign,        color: "text-teal-500",   label: "부서 멘션" },
  RESOLVE:      { icon: CircleCheck,   color: "text-green-500",  label: "완료" },
  GENERAL:      { icon: Info,          color: "text-gray-400",   label: "알림" },
};

export default function NotificationInbox({ accessToken }: { accessToken?: string }) {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<Notification[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // 패널을 사이드바 안에 그리면 사이드바의 overflow 에 잘린다.
  // document.body 로 포털해서 띄우고, 위치는 버튼 좌표에서 직접 계산한다.
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const placePanel = useCallback(() => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;
    const PANEL_W = 320;
    const GAP = 6;
    // 화면 밖으로 나가지 않게 가로 위치를 눌러 준다.
    const left = Math.min(Math.max(8, rect.left), window.innerWidth - PANEL_W - 8);
    setPos({ top: rect.bottom + GAP, left });
  }, []);

  // 열려 있는 동안 스크롤·리사이즈로 버튼이 움직이면 패널도 따라간다.
  useEffect(() => {
    if (!open) return;
    placePanel();
    window.addEventListener("resize", placePanel);
    window.addEventListener("scroll", placePanel, true);
    return () => {
      window.removeEventListener("resize", placePanel);
      window.removeEventListener("scroll", placePanel, true);
    };
  }, [open, placePanel]);

  const headers = useCallback(() => authOnlyHeaders(accessToken), [accessToken]);

  const fetchUnreadCount = useCallback(async () => {
    if (!accessToken) return;
    try {
      const res = await fetch(`${API_URL}/notifications/unread-count`, { headers: headers() });
      if (res.ok) setUnread((await res.json()).count ?? 0);
    } catch { /* 배지 숫자라 실패해도 조용히 넘어간다 */ }
  }, [accessToken, headers]);

  const fetchItems = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    try {
      const q = unreadOnly ? "?unreadOnly=1" : "";
      const res = await fetch(`${API_URL}/notifications${q}`, { headers: headers() });
      if (res.ok) setItems(await res.json());
    } catch { /* 목록은 비워 두고 사용자가 다시 열면 재시도된다 */ } finally {
      setLoading(false);
    }
  }, [accessToken, headers, unreadOnly]);

  // 최초 1회 + 서버가 새 알림을 적재했다고 알려 줄 때만 갱신(폴링 없음)
  useEffect(() => {
    fetchUnreadCount();
    const onInbox = () => { fetchUnreadCount(); if (open) fetchItems(); };
    window.addEventListener("notification-inbox", onInbox);
    return () => window.removeEventListener("notification-inbox", onInbox);
  }, [fetchUnreadCount, fetchItems, open]);

  useEffect(() => { if (open) fetchItems(); }, [open, fetchItems]);

  // 바깥 클릭 시 닫기.
  // 패널이 포털로 body 에 붙어 있어 버튼의 하위가 아니므로 두 요소를 모두 확인해야 한다.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (panelRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Esc 로도 닫는다(포털이라 사이드바 바깥을 덮으므로 탈출 수단이 명확해야 한다).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const markRead = async (id: string) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)));
    setUnread((u) => Math.max(0, u - 1));
    try {
      await fetch(`${API_URL}/notifications/${id}/read`, { method: "PATCH", headers: headers() });
    } catch { fetchUnreadCount(); } // 실패하면 서버 값으로 되맞춘다
  };

  const markAllRead = async () => {
    setItems((prev) => prev.map((n) => ({ ...n, isRead: true })));
    setUnread(0);
    try {
      await fetch(`${API_URL}/notifications/read-all`, { method: "POST", headers: headers() });
    } catch { fetchUnreadCount(); }
    if (unreadOnly) fetchItems();
  };

  const remove = async (id: string, wasUnread: boolean) => {
    setItems((prev) => prev.filter((n) => n.id !== id));
    if (wasUnread) setUnread((u) => Math.max(0, u - 1));
    try {
      await fetch(`${API_URL}/notifications/${id}`, { method: "DELETE", headers: headers() });
    } catch { fetchItems(); }
  };

  const clearRead = async () => {
    setItems((prev) => prev.filter((n) => !n.isRead));
    try {
      await fetch(`${API_URL}/notifications`, { method: "DELETE", headers: headers() });
    } catch { fetchItems(); }
  };

  /** 알림을 클릭하면 읽음 처리하고 해당 위치로 이동한다. */
  const openNotification = (n: Notification) => {
    if (!n.isRead) markRead(n.id);
    setOpen(false);
    if (n.url) router.push(n.url);
  };

  // 패널은 포털로 body 에 붙인다 — 사이드바 안에 두면 overflow 에 잘린다.
  const panel = (
    <div
      ref={panelRef}
      style={{ top: pos?.top ?? 0, left: pos?.left ?? 0 }}
      className="fixed w-80 max-h-[70vh] flex flex-col bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-700 rounded-xl shadow-2xl z-[9999] overflow-hidden"
    >
      {/* 헤더 */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-zinc-800 shrink-0">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-bold text-gray-800 dark:text-zinc-200">알림함</span>
          {unread > 0 && <span className="text-[10px] text-red-500 font-semibold">{unread}개 안 읽음</span>}
        </div>
        <div className="flex items-center gap-0.5">
          <button
            onClick={() => setUnreadOnly((v) => !v)}
            title={unreadOnly ? "전체 보기" : "안 읽은 것만"}
            className={`px-1.5 py-1 rounded text-[10px] font-semibold transition-colors ${
              unreadOnly
                ? "bg-blue-50 text-blue-500 dark:bg-blue-500/15"
                : "text-gray-400 hover:text-gray-600 dark:hover:text-zinc-300"
            }`}
          >
            안 읽음
          </button>
          <button onClick={markAllRead} title="모두 읽음" className="p-1 rounded text-gray-400 hover:text-blue-500">
            <CheckCheck size={13} />
          </button>
          <button onClick={clearRead} title="읽은 알림 비우기" className="p-1 rounded text-gray-400 hover:text-red-500">
            <Trash2 size={13} />
          </button>
          <button onClick={() => setOpen(false)} title="닫기" className="p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-zinc-300">
            <X size={13} />
          </button>
        </div>
      </div>

      {/* 목록 */}
      <div className="overflow-y-auto flex-1">
        {loading && items.length === 0 ? (
          <div className="py-8 text-center text-[11px] text-gray-400">불러오는 중…</div>
        ) : items.length === 0 ? (
          <div className="py-10 text-center text-[11px] text-gray-400 dark:text-zinc-500">
            {unreadOnly ? "안 읽은 알림이 없습니다." : "받은 알림이 없습니다."}
          </div>
        ) : (
          items.map((n) => {
            const meta = TYPE_META[n.type] ?? TYPE_META.GENERAL;
            const Icon = meta.icon;
            return (
              <div
                key={n.id}
                onClick={() => openNotification(n)}
                className={`group flex gap-2 px-3 py-2.5 border-b border-gray-100 dark:border-zinc-800/60 cursor-pointer transition-colors ${
                  n.isRead
                    ? "hover:bg-gray-50 dark:hover:bg-zinc-800/40"
                    : "bg-blue-50/40 dark:bg-blue-500/5 hover:bg-blue-50/70 dark:hover:bg-blue-500/10"
                }`}
              >
                <Icon size={14} className={`${meta.color} shrink-0 mt-0.5`} />
                <div className="flex-1 min-w-0">
                  <p className={`text-[11px] leading-snug ${n.isRead ? "text-gray-500 dark:text-zinc-400" : "text-gray-800 dark:text-zinc-100 font-medium"}`}>
                    {n.message}
                  </p>
                  <span className="text-[9px] text-gray-400 dark:text-zinc-500">
                    {meta.label} · {formatRelativeTime(n.createdAt)}
                  </span>
                </div>
                <div className="shrink-0 flex items-start gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  {!n.isRead && (
                    <button
                      onClick={(e) => { e.stopPropagation(); markRead(n.id); }}
                      title="읽음으로 표시"
                      className="p-1 rounded text-gray-400 hover:text-blue-500"
                    >
                      <Check size={12} />
                    </button>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); remove(n.id, !n.isRead); }}
                    title="삭제"
                    className="p-1 rounded text-gray-400 hover:text-red-500"
                  >
                    <X size={12} />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setOpen((v) => !v)}
        title="알림함"
        className="relative shrink-0 p-1.5 rounded-lg transition-colors border border-gray-200 dark:border-zinc-700 text-gray-400 dark:text-zinc-500 hover:bg-gray-100 dark:hover:bg-zinc-800 bg-white/60 dark:bg-zinc-900/40"
      >
        <Bell size={14} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[15px] h-[15px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-bold leading-none">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>
      {open && pos && typeof document !== "undefined" && createPortal(panel, document.body)}
    </>
  );
}
